import { useCartContext } from '@/context/Cart/CartContext';
import { useDeleteCart } from '@/hooks/cart/useDeleteCart';
import { useDuplicateCart } from '@/hooks/cart/useDuplicateCart';
import { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import { CartItemUi } from '../CartUi';
import { ContextualMenuUi, initialState } from './ContextualMenuUi';

interface ContextualMenuViewControllerProps {
  order: CartItemUi;
}

export function useContextualMenuViewController({
  order,
}: ContextualMenuViewControllerProps) {
  const { setOpenModal } = useCartContext();
  const deleteCartHook = useDeleteCart();
  const duplicateCartHook = useDuplicateCart();
  const [uiState, setUiState] = useImmer<ContextualMenuUi>(initialState);
  const anchorEl = useRef<null | HTMLElement>(null);

  useEffect(() => {
    setUiState((draft) => {
      draft.showEdit = order.badgeStatus?.statusId === 'draft';
      draft.showView = order.badgeStatus?.statusId === 'created';
    });
  }, [order.badgeStatus?.statusId]);

  const handleClickOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    anchorEl.current = event.currentTarget;
    setUiState((prev) => {
      prev.open = true;
    });
  };

  const handleClose = () =>
    setUiState((prev) => {
      prev.open = false;
    });

  const onClickedViewCart = () => {
    setOpenModal?.({
      type: 'view',
      cartId: order.cartId,
      cartNumber: order.cartNumber,
    });
    handleClose();
  };

  const onClickedEditCart = () => {
    setOpenModal?.({
      type: 'edit',
      cartId: order.cartId,
      cartNumber: order.cartNumber,
    });
    handleClose();
  };

  const onClickedDeleteCart = () =>
    setUiState((draft) => {
      draft.deleteDialog = {
        cancelButton: { label: 'Cancelar', variant: 'outlined' },
        confirmButton: { label: 'Eliminar', variant: 'contained' },
      };
      draft.open = false;
    });

  const handleCancelDeleteDialog = () =>
    setUiState((draft) => {
      draft.deleteDialog = undefined;
    });

  const handleConfirmDelete = async () => {
    setUiState((draft) => {
      if (draft.deleteDialog?.confirmButton)
        draft.deleteDialog.confirmButton.loading = true;
      if (draft.deleteDialog?.cancelButton)
        draft.deleteDialog.cancelButton.disabled = true;
    });
    await new Promise((resolve) => setTimeout(resolve, 2000));
    deleteCartHook.mutate(order.cartId, {
      onSettled: () =>
        setUiState((draft) => {
          draft.open = false;
          draft.deleteDialog = undefined;
        }),
    });
  };

  const onClickedDuplicateCart = () =>
    setUiState((draft) => {
      draft.duplicateDialog = {
        cancelButton: { label: 'Cancelar', variant: 'outlined' },
        confirmButton: { label: 'Duplicar', variant: 'contained' },
      };
      draft.open = false;
    });

  const handleCancelDuplicateDialog = () =>
    setUiState((draft) => {
      draft.duplicateDialog = undefined;
    });

  const handleConfirmDuplicate = async () => {
    setUiState((draft) => {
      if (draft.duplicateDialog?.confirmButton)
        draft.duplicateDialog.confirmButton.loading = true;
      if (draft.duplicateDialog?.cancelButton)
        draft.duplicateDialog.cancelButton.disabled = true;
    });
    await new Promise((resolve) => setTimeout(resolve, 2000));
    duplicateCartHook.mutate(order.cartId, {
      onSettled: () =>
        setUiState((draft) => {
          draft.open = false;
          draft.duplicateDialog = undefined;
        }),
    });
  };

  return {
    uiState,
    actions: {
      handleClickOpenMenu,
      onClickedViewCart,
      onClickedEditCart,
      onClickedDeleteCart,
      onClickedDuplicateCart,
      handleClose,
      handleConfirmDelete,
      handleConfirmDuplicate,
      handleCancelDeleteDialog,
      handleCancelDuplicateDialog,
    },
    references: {
      anchorEl,
    },
  };
}
